import type { PlayerType } from "@/lib/coworld-player-types";
import type { PreviewDetails } from "../content/preview-details.types";
import type { PreviewDoor } from "../copy/preview-copy.types";
import { recordOf, safeManifestUrl } from "./manifest-record";
import { developerFrom } from "./coworld-source";
import { manifestRulesDocPage } from "./manifest-facts";
function publisherFrom(commissionerKey: string): PreviewDetails["publisher"] {
  return commissionerKey === "platform"
    ? { name: "Softmax", href: null }
    : null;
}
function releasedFrom({
  leagueCreatedAt,
  gameCreatedAt,
}: {
  leagueCreatedAt: string;
  gameCreatedAt: string;
}): PreviewDetails["released"] {
  return {
    at: gameCreatedAt,
    label:
      new Date(gameCreatedAt).getTime() > new Date(leagueCreatedAt).getTime()
        ? "Updated"
        : "Released",
  };
}
const reservedTagWords = new Set(["tag", "tags"]);
export function detailsFrom(params: {
  manifest: unknown;
  documentationUrl: string | null;
  leagueCreatedAt: string;
  gameCreatedAt: string;
  commissionerKey: string;
  seats: number | null;
  playerTypes: readonly PlayerType[] | null;
  source: PreviewDoor | null;
  guideHref: string;
  discordHref: string;
}): PreviewDetails {
  const manifestObj = recordOf(params.manifest);
  const game = recordOf(manifestObj.game);
  const docs = recordOf(game.docs);
  const version = typeof game.version === "string" ? game.version : null;
  const tags = manifestObj.tags;
  const tagList = Array.isArray(tags)
    ? tags.filter(
        (t): t is string =>
          typeof t === "string" &&
          t.trim() !== "" &&
          !reservedTagWords.has(t.trim().toLowerCase()),
      )
    : [];
  const variants = manifestObj.variants;
  const modes = Array.isArray(variants)
    ? variants
        .map((v) => recordOf(v).name)
        .filter((n): n is string => typeof n === "string")
    : [];
  const readme = recordOf(docs.readme);
  const readmeHref =
    readme.type === "uri" ? safeManifestUrl(readme.value) : null;
  const docsHref =
    params.documentationUrl &&
    !params.documentationUrl.startsWith("https://softmax.com/docs/")
      ? params.documentationUrl
      : null;
  const rulesHref = manifestRulesDocPage(params.manifest)?.href ?? null;
  const links: (PreviewDoor | null)[] = [
    params.source
      ? {
          title: "Source",
          subtitle: params.source.subtitle,
          href: params.source.href,
        }
      : null,
    readmeHref ? { title: "README", subtitle: "", href: readmeHref } : null,
    rulesHref ? { title: "Rules", subtitle: "", href: rulesHref } : null,
    docsHref ? { title: "Docs", subtitle: "", href: docsHref } : null,
    { title: "Discord", subtitle: "", href: params.discordHref },
    {
      title: "Agent guide (play.md)",
      subtitle: "",
      href: params.guideHref,
    },
  ];
  return {
    developer: developerFrom(params.manifest),
    publisher: publisherFrom(params.commissionerKey),
    released: releasedFrom({
      leagueCreatedAt: params.leagueCreatedAt,
      gameCreatedAt: params.gameCreatedAt,
    }),
    leagueOpenedAt: params.leagueCreatedAt,
    version,
    playerTypes: params.playerTypes,
    seats: params.seats,
    modes,
    tags: tagList,
    links: links.filter((d): d is PreviewDoor => d !== null),
  };
}
